import { useEffect, useState } from 'react';
import axios from 'axios';
import { X, AlertTriangle, FileText, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import BureauBadges from './BureauBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reasons pulled from the FCRA dispute canon — kept short to preserve the
// modal footprint. Users can add a free-text explanation.
const DISPUTE_REASONS = [
  { id: 'wrong_balance',   label: 'Balance is incorrect' },
  { id: 'wrong_status',    label: 'Payment status is wrong' },
  { id: 'wrong_dates',     label: 'Date opened / reported is wrong' },
  { id: 'not_mine',        label: 'This account is not mine' },
  { id: 'paid_in_full',    label: 'Already paid in full' },
  { id: 'reaged',          label: 'Account has been re-aged' },
  { id: 'settled',         label: 'Settled — should not show as outstanding' },
  { id: 'duplicate',       label: 'Duplicate listing' },
];

const FIELD_LABEL = {
  balance:       'Balance',
  status:        'Payment status',
  date_reported: 'Date reported',
  issue_type:    'Issue type',
  account_type:  'Account type',
};

export default function CrossBureauCompareModal({ open, onClose, creditor, last4 }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selectedBureaus, setSelectedBureaus] = useState({});
  const [reason, setReason] = useState('wrong_balance');
  const [explanation, setExplanation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setData(null);
    setSuccess(null);
    setExplanation('');
    axios
      .get(`${API_URL}/api/debt/compare-across-bureaus`, {
        params: { creditor, last4 },
      })
      .then((res) => {
        setData(res.data);
        // Default: pre-select bureaus that show any disagreement.
        const disagreeing = {};
        if (res.data?.rows) {
          res.data.rows.forEach((r) => { disagreeing[r.bureau] = res.data.has_any_difference; });
        }
        setSelectedBureaus(disagreeing);
      })
      .catch(() => toast.error('Could not load bureau comparison.'))
      .finally(() => setLoading(false));
  }, [open, creditor, last4]);

  if (!open) return null;

  const toggleBureau = (b) => setSelectedBureaus((s) => ({ ...s, [b]: !s[b] }));
  const selectedList = Object.entries(selectedBureaus).filter(([, v]) => v).map(([k]) => k);

  const submit = async () => {
    if (selectedList.length === 0) {
      toast.error('Pick at least one bureau to dispute.');
      return;
    }
    setSubmitting(true);
    try {
      const { data: resp } = await axios.post(`${API_URL}/api/dispute/bureau-specific`, {
        creditor,
        last4,
        bureaus: selectedList,
        reason,
        custom_explanation: explanation || null,
      });
      setSuccess(resp);
      toast.success(
        `Drafted ${resp.disputes_created} dispute letter${resp.disputes_created === 1 ? '' : 's'}`,
        { description: `For ${selectedList.join(' + ')}. Open Disputes to review and mail.` },
      );
      window.dispatchEvent(new CustomEvent('selah:disputes-updated'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not draft the letter.');
    } finally {
      setSubmitting(false);
    }
  };

  const rows = data?.rows || [];
  const diffs = data?.differences || {};

  // A cell should highlight when this bureau's value differs from at least
  // one other bureau's value for the same field.
  const isCellDifferent = (field, thisValue) => {
    if (!diffs[field]) return false;
    const values = rows.map((r) => String(r[field] || '').trim());
    const norm = String(thisValue || '').trim();
    return values.filter((v) => v && v !== norm).length > 0;
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#36454F]/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="cross-bureau-modal"
    >
      <div
        className="bg-white rounded-sm w-full max-w-4xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[#36454F]/10 flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-['Manrope'] font-semibold">
              Cross-bureau comparison
            </p>
            <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mt-0.5">
              {creditor} <span className="text-[#36454F]/40 font-mono text-base">•••• {last4}</span>
            </h2>
            {data && (
              <p className="text-xs text-[#36454F]/60 font-['Manrope'] mt-1">
                Reported by {data.bureaus_count} bureau{data.bureaus_count === 1 ? '' : 's'} · {' '}
                {data.has_any_difference ? (
                  <span className="text-[#B76E79] font-semibold">disagreements detected</span>
                ) : (
                  <span>everything matches</span>
                )}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-[#36454F]/40 hover:text-[#36454F] rounded-sm"
            data-testid="cross-bureau-close"
          >
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {loading && (
          <div className="p-12 flex items-center justify-center text-[#36454F]/50" data-testid="cross-bureau-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" strokeWidth={1.5} />
            Comparing across bureaus…
          </div>
        )}

        {success && (
          <div className="p-6 bg-[#4CAF50]/8 border-t border-[#4CAF50]/25 flex items-start gap-3" data-testid="cross-bureau-success">
            <CheckCircle2 className="w-5 h-5 text-[#4CAF50] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="text-sm text-[#36454F] font-['Manrope'] font-semibold">
                Drafted {success.disputes_created} letter{success.disputes_created === 1 ? '' : 's'}
              </p>
              <p className="text-xs text-[#36454F]/70 font-['Manrope'] mt-1">
                Open the Disputes page to review, download, and mail each letter certified.
              </p>
              <div className="mt-3 flex gap-2">
                <a href="/disputes">
                  <Button size="sm" className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm">
                    Go to disputes
                  </Button>
                </a>
                <Button size="sm" variant="outline" onClick={onClose} className="border-[#36454F]/20 text-[#36454F] font-['Manrope'] rounded-sm">
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {data && !success && (
          <>
            {/* Difference banner */}
            {data.has_any_difference && (
              <div className="mx-6 mt-5 p-3 bg-[#B76E79]/8 border border-[#B76E79]/25 rounded-sm flex items-start gap-2" data-testid="cross-bureau-diff-banner">
                <AlertTriangle className="w-4 h-4 text-[#B76E79] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-xs text-[#36454F]/85 font-['Manrope']">
                  <strong>These bureaus disagree.</strong> Highlighted cells below don't match across all three. Under FCRA §611, you can dispute the one that's wrong.
                </p>
              </div>
            )}

            {/* Comparison table */}
            <div className="p-6 overflow-x-auto">
              <table className="w-full text-sm font-['Manrope']" data-testid="cross-bureau-table">
                <thead>
                  <tr className="text-left border-b border-[#36454F]/10">
                    <th className="pb-2 pr-4 text-xs uppercase tracking-wider text-[#36454F]/50 font-semibold">Field</th>
                    {rows.map((r) => (
                      <th key={r.bureau} className="pb-2 px-2 whitespace-nowrap">
                        <BureauBadges bureaus={[r.bureau]} size="xs" showLabel />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {['balance', 'status', 'date_reported', 'issue_type', 'account_type'].map((field) => (
                    <tr key={field} className="border-b border-[#36454F]/5">
                      <td className="py-2.5 pr-4 text-[#36454F]/60 whitespace-nowrap">{FIELD_LABEL[field]}</td>
                      {rows.map((r) => {
                        const val = r[field];
                        const highlight = isCellDifferent(field, val);
                        return (
                          <td
                            key={r.bureau}
                            className={`py-2.5 px-2 ${highlight ? 'bg-[#B76E79]/10 text-[#B76E79] font-semibold' : 'text-[#36454F]'}`}
                            data-testid={`compare-cell-${r.bureau.toLowerCase()}-${field}`}
                          >
                            {val || <span className="text-[#36454F]/30">—</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  <tr>
                    <td className="py-2.5 pr-4 text-[#36454F]/60 whitespace-nowrap">Account number</td>
                    {rows.map((r) => (
                      <td key={r.bureau} className="py-2.5 px-2 text-[#36454F]/70 font-mono text-xs">
                        {r.account_number || '—'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Dispute preparation */}
            <div className="border-t border-[#36454F]/10 p-6 bg-[#F5F5F5]">
              <p className="text-[10px] uppercase tracking-widest text-[#B76E79] font-['Manrope'] font-semibold mb-1">
                Draft a dispute letter — pick who to send it to
              </p>
              <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#36454F] mb-3">
                Which bureau(s) are wrong?
              </h3>
              <div className="flex flex-wrap gap-3 mb-4">
                {rows.map((r) => (
                  <label
                    key={r.bureau}
                    className="flex items-center gap-2 bg-white border border-[#36454F]/15 rounded-sm px-3 py-2 cursor-pointer hover:border-[#B76E79]"
                    data-testid={`dispute-bureau-toggle-${r.bureau.toLowerCase()}`}
                  >
                    <Checkbox
                      checked={!!selectedBureaus[r.bureau]}
                      onCheckedChange={() => toggleBureau(r.bureau)}
                    />
                    <BureauBadges bureaus={[r.bureau]} size="xs" showLabel />
                  </label>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[#36454F]/60 font-['Manrope'] mb-1 block">Reason for dispute</label>
                  <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#36454F]/15 rounded-sm bg-white font-['Manrope'] text-[#36454F] focus:outline-none focus:border-[#B76E79]"
                    data-testid="dispute-reason-select"
                  >
                    {DISPUTE_REASONS.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-[#36454F]/60 font-['Manrope'] mb-1 block">Optional details</label>
                  <input
                    type="text"
                    value={explanation}
                    onChange={(e) => setExplanation(e.target.value)}
                    placeholder="e.g. Correct balance is $2,450 per statement"
                    className="w-full px-3 py-2 text-sm border border-[#36454F]/15 rounded-sm bg-white font-['Manrope'] text-[#36454F] focus:outline-none focus:border-[#B76E79]"
                    data-testid="dispute-explanation-input"
                  />
                </div>
              </div>

              <div className="mt-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <p className="text-xs text-[#36454F]/55 font-['Manrope']">
                  {selectedList.length === 0
                    ? 'Select at least one bureau above.'
                    : `You'll draft ${selectedList.length} letter${selectedList.length === 1 ? '' : 's'} — ${selectedList.join(' + ')}.`}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="border-[#36454F]/20 text-[#36454F] font-['Manrope'] rounded-sm"
                    data-testid="cross-bureau-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submit}
                    disabled={submitting || selectedList.length === 0}
                    className="bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                    data-testid="cross-bureau-submit-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.5} />Drafting…</>
                    ) : (
                      <><FileText className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Draft dispute letter{selectedList.length === 1 ? '' : 's'}</>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
